import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  InventoryResponse,
  StockAdjustRequest,
  StockTransactionResponse,
} from '@/types/inventory';
import type {
  InventoryBackorderResponse,
  InventoryLedgerEntry,
  InventoryReadinessBundleResponse,
  InventorySerialRegisterRequest,
  InventorySerialResponse,
  InventoryTransferCreateRequest,
  InventoryTransferResponse,
  StockAvailabilityResponse,
} from '@/types/inventoryPlatform';

export const inventoryApi = {
  list: (params?: SearchParams & { storeId?: number; productId?: number }) =>
    unwrap(api.get<ApiResponse<PageResponse<InventoryResponse>>>('/v1/inventory', { params })),

  get: (id: number) => unwrap(api.get<ApiResponse<InventoryResponse>>(`/v1/inventory/${id}`)),

  adjust: (body: StockAdjustRequest) =>
    unwrap(api.post<ApiResponse<StockTransactionResponse>>('/v1/inventory/adjust', body)),

  listTransactions: (
    params?: SearchParams & { storeId?: number; transactionTypes?: string[] },
  ) =>
    unwrap(
      api.get<ApiResponse<PageResponse<StockTransactionResponse>>>('/v1/inventory/transactions', {
        params,
      }),
    ),

  availability: (storeId: number, productId: number) =>
    unwrap(
      api.get<ApiResponse<StockAvailabilityResponse>>('/v1/inventory/availability', {
        params: { storeId, productId },
      }),
    ),

  listTransfers: (params?: SearchParams & { fromStoreId?: number }) =>
    unwrap(
      api.get<ApiResponse<PageResponse<InventoryTransferResponse>>>('/v1/inventory/transfers', {
        params,
      }),
    ),

  getTransfer: (id: number) =>
    unwrap(api.get<ApiResponse<InventoryTransferResponse>>(`/v1/inventory/transfers/${id}`)),

  createTransfer: (body: InventoryTransferCreateRequest) =>
    unwrap(api.post<ApiResponse<InventoryTransferResponse>>('/v1/inventory/transfers', body)),

  submitTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<InventoryTransferResponse>>(`/v1/inventory/transfers/${id}/submit`)),

  approveTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<InventoryTransferResponse>>(`/v1/inventory/transfers/${id}/approve`)),

  dispatchTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<InventoryTransferResponse>>(`/v1/inventory/transfers/${id}/dispatch`)),

  receiveTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<InventoryTransferResponse>>(`/v1/inventory/transfers/${id}/receive`)),

  cancelTransfer: (id: number) =>
    unwrap(api.post<ApiResponse<InventoryTransferResponse>>(`/v1/inventory/transfers/${id}/cancel`)),

  cancelStockAdjustment: (id: number) =>
    unwrap(api.post<ApiResponse<StockTransactionResponse>>(`/v1/inventory/adjust/${id}/cancel`)),

  readinessBundle: () =>
    unwrap(
      api.get<ApiResponse<InventoryReadinessBundleResponse>>('/v1/inventory/platform/reports/readiness'),
    ),

  platformLedger: (params?: SearchParams) =>
    unwrap(
      api.get<ApiResponse<PageResponse<InventoryLedgerEntry>>>('/v1/inventory/platform/ledger', { params }),
    ),

  listBackorders: (params?: SearchParams & { storeId?: number }) =>
    unwrap(
      api.get<ApiResponse<PageResponse<InventoryBackorderResponse>>>('/v1/inventory/backorders', { params }),
    ),

  allocateBackorder: (id: number) =>
    unwrap(api.post<ApiResponse<InventoryBackorderResponse>>(`/v1/inventory/backorders/${id}/allocate`)),

  listSerials: (params?: SearchParams & { productId?: number }) =>
    unwrap(
      api.get<ApiResponse<PageResponse<InventorySerialResponse>>>('/v1/inventory/serials', { params }),
    ),

  registerSerial: (body: InventorySerialRegisterRequest) =>
    unwrap(api.post<ApiResponse<InventorySerialResponse>>('/v1/inventory/serials', body)),

  updateSerialStatus: (id: number, statusCode: string) =>
    unwrap(
      api.post<ApiResponse<InventorySerialResponse>>(`/v1/inventory/serials/${id}/status`, null, {
        params: { statusCode },
      }),
    ),
};
